export const landingPage = {
  title: 'Landing Pages',
  subtitle: 'Generate SEO-optimized landing pages powered by AI keyword analysis',

  actions: {
    new: 'New Project',
  },

  empty: {
    title: 'No landing page projects yet',
    description: 'Create SEO-optimized landing pages in minutes using AI-powered keyword analysis and content generation.',
    cta: 'Create your first project',
    feature1: 'AI keyword analysis',
    feature2: 'Anti-cannibalization',
    feature3: 'Bulk generation',
  },

  stats: {
    projects: '{{count}} projects',
    completed: '{{count}} completed',
    pages: '{{count}} pages total',
    projectsLabel: 'Projects',
    completedLabel: 'Completed',
    pagesLabel: 'Pages Generated',
  },

  relativeTime: {
    justNow: 'Just now',
    minutesAgo: '{minutes}m ago',
    hoursAgo: '{hours}h ago',
    daysAgo: '{days}d ago',
  },

  status: {
    draft: 'Draft',
    analyzing: 'Analyzing',
    analyzed: 'Analyzed',
    generating: 'Generating',
    completed: 'Completed',
    failed: 'Failed',
  },

  delete: {
    title: 'Delete Project',
    description: 'This action cannot be undone. The project and all its landing pages will be permanently deleted.',
  },

  wizard: {
    title: 'Landing Page Generator',
    subtitle: 'Create SEO-optimized landing pages in 3 steps',

    steps: {
      configuration: 'Configuration',
      analysis: 'Keyword Analysis',
      generation: 'Content Generation',
    },

    config: {
      sectionProject: 'Project',
      sectionSources: 'Sources',
      sectionLayout: 'Layout',
      projectName: 'Project Name',
      projectNamePlaceholder: 'e.g., Q1 Product Landing Pages',
      website: 'Website',
      websitePlaceholder: 'Select a website...',
      websiteHint: 'Used for anti-cannibalization checks against existing pages',
      brandVoice: 'Brand Voice',
      brandVoicePlaceholder: 'Select a brand voice...',
      keywordResearch: 'Keyword Research',
      keywordResearchPlaceholder: 'Select a keyword research...',
      layout: 'Layout Definition',
      layoutTemplate: 'Template',
      layoutDescription: 'Description',
      layoutImage: 'Image',
      layoutTemplateDesc: 'Use a predefined structure',
      layoutDescriptionDesc: 'Describe the layout in text',
      layoutImageDesc: 'Upload a reference image',
      layoutDescriptionPlaceholder: 'Describe how you want the landing page structured...',
      layoutTemplateSoon: 'Template library coming soon. Use description mode to define your layout.',
      layoutImageSoon: 'Image upload coming soon. Use description mode to define your layout.',
      createAndAnalyze: 'Create & Analyze',
      update: 'Update',
      keywordSource: 'Keywords',
      existingResearch: 'Existing Research',
      uploadCsv: 'Upload CSV',
      csvDropzone: 'Drop CSV file here or click to browse',
      csvParsed: '{{count}} keywords loaded',
      csvTruncated: 'Showing first 100 of {{total}} keywords',
      csvFormatHint: 'Google Keyword Planner CSV format',
      csvFileTab: 'Upload File',
      csvPasteTab: 'Paste List',
      pastePlaceholder: 'best crm software, 5400\ncrm for small business, 2900\nemail marketing tool\n...',
      pasteFormatHint: 'One keyword per line. Optionally add volume after a comma or tab:\nkeyword, volume  or  keyword\\tvolume',
      pasteProcess: 'Process',
      csvParseError: 'Could not parse CSV',
      selectKeywordSourceHint: 'Select a keyword source to continue',
      selectKeywordResearchHint: 'Select a keyword research to continue',
      sectionReferences: 'Reference Materials',
      referenceHint: 'Upload screenshots, HTML, PDFs, or DOCX to help the AI understand your existing landing structure.',
      referenceUploadFile: 'Upload File',
      referenceUploadHtml: 'Paste HTML',
      referenceDropzone: 'Drop file here or click to browse',
      referenceDropzoneFormats: 'PNG, JPG, WEBP, PDF, DOCX · max 10MB',
      referenceHtmlPlaceholder: 'Paste HTML source code...',
      referenceProcess: 'Add',
      referenceQueued: 'Queued',
      referenceDelete: 'Remove',
      referenceMaxReached: 'Maximum 5 references per project',
      layoutReference: 'From References',
      layoutReferenceDesc: 'Use uploaded landing structure',
      layoutHybrid: 'Hybrid',
      layoutHybridDesc: 'Description + references',
      layoutHybridDescriptionPlaceholder: 'Describe additional layout instructions...',
    },

    analysis: {
      readyToAnalyze: 'Ready to analyze keywords from your research. This will check for cannibalization and prioritize keywords.',
      startAnalysis: 'Start Analysis',
      analyzing: 'Analyzing keywords and checking for cannibalization...',
      analysisSteps: {
        extracting: 'Extracting keywords from research',
        evaluating: 'Evaluating search intent & relevance',
        checking: 'Checking cannibalization against existing pages',
        prioritizing: 'Prioritizing keywords by opportunity score',
        grouping: 'Grouping recommendations',
        finishing: 'Finalizing analysis results',
      },
      selectedCount: '{{count}} of {{total}} keywords selected',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      approveAndGenerate: 'Approve {{count}} & Generate',
      suggestedTitle: 'Suggested title',
      alignment: 'Alignment',
      existingPageFound: 'Existing page found',
      group: {
        recommended: 'Recommended',
        consider: 'Consider',
        discard: 'Discard',
      },
      showDiscarded: 'Show {{count}} discarded',
      hideDiscarded: 'Hide discarded',
      tabs: {
        prioritization: 'Prioritization',
        prioritizationShort: 'Priority',
        cannibalization: 'Cannibalization',
        cannibalizationShort: 'Cannibal.',
        diagnostic: 'Diagnostic',
        diagnosticShort: 'Diag.',
        verdict: 'Verdict & Gaps',
        verdictShort: 'Verdict',
      },
      columns: {
        title: 'Suggested Title',
        keyword: 'Keyword',
        volume: 'Volume',
        intent: 'Intent',
        feature: 'Feature',
        rec: 'Rec.',
        onSite: 'On Site',
        featureBacking: 'Feature Backing',
        landing: 'Landing',
        pillar: 'Pillar',
        exclusive: 'Exclusive',
        overlapsWith: 'Overlaps With',
        observation: 'Observation',
        featureName: 'Feature',
        description: 'Description',
      },
      intent: {
        purchase: 'Purchase',
        investigation: 'Investigation',
        informational: 'Informational',
      },
      featureBacked: 'Feature backed',
      noFeature: 'No feature',
      exclusive: 'Exclusive',
      overlapping: 'Overlapping',
      overlapsWithLabel: 'Overlaps with',
      verdictTitle: 'Strategic Verdict',
      coverageGaps: 'Coverage Gaps',
      allCovered: 'All company features are covered by the proposed landing pages.',
      noCannibalization: 'No cannibalization risks detected. All keywords target unique content.',
    },

    generation: {
      preparing: 'Preparing generation...',
      generatingFor: 'Generating content for "{{keyword}}"...',
      completedPages: 'Completed Pages',
      preview: 'Preview',
      edit: 'Edit',
      export: 'Export',
      copyKeyword: 'Copy',
      exportAll: 'Export All',
      failed: 'failed',
      livePreview: 'Live Preview',
      queued: 'queued',
      inProgress: 'Generation in progress',
      pagesGenerated: '{{count}} pages generated',
      retryFailed: 'Retry Failed',
      errorTitle: 'Generation could not be completed',
      errorHint: 'Try again using the "Retry Failed" button. If the problem persists, check your project configuration.',
      errors: {
        GENERATION_FAILED: 'The content generation service encountered an unexpected error. Please try again.',
        LANDING_PAGE_INVALID_STATE: 'This project is not ready for generation. Go back and verify your keyword selection.',
        LANDING_PAGE_PROJECT_NOT_FOUND: 'The project could not be found. It may have been deleted.',
        LANDING_PAGE_NO_APPROVED_KEYWORDS: 'No approved keywords found. Go back and select at least one keyword.',
      },
    },
  },

  quality: {
    good: 'Quality',
    needsImprovement: 'Review',
    poor: 'Low Quality',
  },

  references: {
    uploadSuccess: 'Reference uploaded successfully',
    uploadError: 'Failed to upload reference',
    deleteSuccess: 'Reference deleted',
    deleteError: 'Failed to delete reference',
  },

  preview: {
    seoTitle: 'SEO Title',
    metaDescription: 'Meta Description',
    h1: 'H1 Heading',
    sections: 'Sections',
    wireframe: 'Wireframe',
    edit: 'Edit',
    export: 'Export as DOCX',
    schemaMarkup: 'Schema Markup (JSON-LD)',
    copySchema: 'Copy Schema',
    sectionsTab: 'Sections',
    htmlPreview: 'HTML Preview',
    copyHtml: 'Copy HTML',
    exportHtml: 'Export HTML',
    htmlCopied: 'Copied!',
    responsive: {
      mobile: 'Mobile',
      tablet: 'Tablet',
      desktop: 'Desktop',
    },
  },

  editor: {
    title: 'Edit: {{keyword}}',
    unsavedWarning: 'You have unsaved changes. Discard them?',
  },

  quickCreate: {
    title: 'Quick Create Landing',
    subtitle: 'Generate a landing page from your content plan keyword',
    back: 'Back to plan',
    nameLabel: 'Project name',
    namePlaceholder: 'Landing: your keyword',
    volume: '{{count}} monthly searches',
    generateButton: 'Generate Landing',
    dropzoneText: 'Drop reference files here or click to browse',
    dropzoneHint: 'PNG, JPG, WebP, PDF, HTML — up to {{max}} files',
    generating: 'Generating your landing page...',
    generatingDescription: 'This may take a few seconds. We are creating the project, approving the keyword, and generating the content.',
    toast: {
      success: 'Landing page generated successfully',
      error: 'Failed to generate landing page',
    },
  },

  toast: {
    createSuccess: 'Landing project created successfully',
    createError: 'Error creating project: {{message}}',
    deleteSuccess: 'Landing project deleted',
    deleteError: 'Error deleting project: {{message}}',
    analysisSuccess: 'Keyword analysis completed',
    analysisError: 'Analysis failed: {{message}}',
    approvalSuccess: 'Keywords approved',
    approvalError: 'Error approving keywords: {{message}}',
    exportSuccess: 'Export downloaded successfully',
    exportError: 'Export failed: {{message}}',
  },
} as const
